import "twin.macro";

import { useForm } from "react-hook-form";

import { Alert } from "@mui/material";

import { format } from "date-fns";
import _ from "lodash";

import useConfirm from "@features/confirm";
import { LoadingButton } from "@features/ui";
import { DemandPlan } from "@models/DemandPlan";
import {
  ControlledDateInput,
  ControlledResourceAutocomplete,
  ControlledTextInput,
} from "@utils/forms";

import {
  useCreateDemandPlanMutation,
  useDemandPlanQuery,
  useUpdateDemandPlanMutation,
} from "./data/demandPlanQueries";

export const DemandPlanForm = ({
  planId,
  onSaveSuccess,
}: {
  planId?: string;
  onSaveSuccess?: (demandPlan: DemandPlan) => void;
}) => {
  const { data: plan, isLoading } = useDemandPlanQuery(planId);

  const createMutation = useCreateDemandPlanMutation();
  const updateMutation = useUpdateDemandPlanMutation();

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const formMethods = useForm({
    defaultValues: {
      name: plan
        ? plan.name
        : `Demand Plan | ${format(new Date(), "M/d/yyyy")}`,
      orderWindows: plan ? plan.orderWindows : [],
      expectedPurchaseOrderDate: plan
        ? plan.expectedPurchaseOrderDate
        : format(new Date(), "yyyy-MM-dd"),
    },
    mode: "onBlur",
  });

  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = formMethods;

  const confirm = useConfirm();

  const handleSave = async (values) => {
    const { orderWindows: orderWindowIds, ...payload } = values;
    const { expectedPurchaseOrderDate } = payload;
    const orderWindows = orderWindowIds.map((id) => ({
      id: typeof id === "object" ? id.id : id,
      type: "order-window",
    }));

    payload.orderWindows = orderWindows;

    if (plan) {
      let approvedToSubmit = true;
      const orderWindowsChanged = !_.isEqual(
          plan.orderWindows.map((ow) => ow.id),
          orderWindows.map((ow) => ow.id)
        ),
        expectedPODateChanged =
          expectedPurchaseOrderDate !== plan.expectedPurchaseOrderDate,
        rebuildChanges = [
          ...[orderWindowsChanged ? ["order windows"] : []],
          ...[expectedPODateChanged ? ["expected purchase order date"] : []],
        ].flat();

      // Prompt for alert when changing order windows or expected purchase order date has changed
      // The changes cause the report to be rebuilt and any progress will be lost
      if (rebuildChanges.length) {
        const changesText = rebuildChanges.map(
          (changeText, index, allChanges) => {
            return (
              (allChanges.length === 1 || index === 0
                ? ""
                : ++index < allChanges.length
                  ? ", "
                  : " and ") + changeText
            );
          }
        );

        approvedToSubmit = await confirm(
          <span>
            Modifying the {changesText} will rebuild the demand plan and
            <b>
              &nbsp;any changes that have been made to this demand plan will be
              lost.
            </b>
          </span>
        );
      }

      if (approvedToSubmit) {
        updateMutation.mutate(
          { id: plan.id, ...payload },
          {
            onSuccess: (dp) => {
              onSaveSuccess?.(dp);
            },
          }
        );
      }
    } else {
      createMutation.mutate(payload, {
        onSuccess: (dp) => {
          onSaveSuccess?.(dp);
        },
      });
    }
  };

  return (
    <form>
      <div tw="space-y-4">
        <ControlledTextInput
          label="Name"
          name="name"
          control={control}
          rules={{ required: "Name is required" }}
        />
        <ControlledResourceAutocomplete
          name="orderWindows"
          label="Order Windows"
          resource="order-windows"
          multiple
          disableCloseOnSelect
          control={control}
          rules={{ required: "Select an order window" }}
          requestParams={{
            filter: {
              assignedToDemandPlan: false,
              hasDemandCoverageEndDate: true,
              isReviewClosed: true,
            },
          }}
        />
        <ControlledDateInput
          name="expectedPurchaseOrderDate"
          label="Expected Purchase Order Date"
          control={control}
          rules={{ required: "Date is required for calculation" }}
        />
      </div>
      {createMutation.isError && (
        <div tw="pt-4">
          <Alert severity="error">{createMutation.error.message}</Alert>
        </div>
      )}
      {updateMutation.isError && (
        <div tw="pt-4">
          <Alert severity="error">{updateMutation.error.message}</Alert>
        </div>
      )}
      {!isLoading && (
        <div tw="flex justify-end py-4">
          <LoadingButton
            variant="contained"
            loading={isSaving}
            disabled={!isDirty}
            onClick={handleSubmit(handleSave)}
          >
            {plan ? "Update" : "Create"} Plan
          </LoadingButton>
        </div>
      )}
    </form>
  );
};
